/**
 * Canonical multi-variation publication snapshot.
 * Publication writes from this explicit matrix only — variants are never
 * derived at provider-write time.
 */
import {
  approvedOverrideThenCanonical,
  approvedOverrideThenCanonicalBool,
  approvedOverrideThenCanonicalInt,
} from "./storefront-precedence";
import { effectiveSellingPrice } from "@/lib/products";
import { resolveShippingFacts } from "@/lib/shipping-facts";
import { pickProductForProvider } from "./provider-outbound-allowlist";

export type IdentifierPolicyMode = "omitted" | "does_not_apply" | "provided";

export interface CanonicalOptionDefinition {
  name: string;
  values: string[];
}

export interface CanonicalVariant {
  id?: string | null;
  sku: string;
  option_values: Record<string, string>;
  price: number;
  quantity: number;
  currency: string;
  image_url?: string | null;
  gtin?: string | null;
}

export interface CanonicalPublicationSnapshot {
  product: {
    title: string;
    description: string;
    brand?: string | null;
    mpn?: string | null;
    condition?: string | null;
    category?: string | null;
    weight_grams?: number | null;
    dimensions_cm?: { length: number; width: number; height: number } | null;
  };
  images: string[];
  options: CanonicalOptionDefinition[];
  variants: CanonicalVariant[];
  identifier_policy: {
    mode: IdentifierPolicyMode;
    gtin?: string | null;
  };
  storefront?: {
    seo_title: string | null;
    meta_description: string | null;
    seo_keywords: string | null;
    url_slug: string | null;
    og_title: string | null;
    og_description: string | null;
    og_image_url: string | null;
    storefront_visible: boolean;
    is_featured: boolean;
    min_purchase_qty: number | null;
    max_purchase_qty: number | null;
  };
}

export type SnapshotIssue = { code: string; message: string };

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? v as Record<string, unknown> : null;
}

function optionMapFromUnknown(raw: unknown): Record<string, string> {
  const obj = asRecord(raw);
  if (obj) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === "string" && v.trim()) out[k] = v.trim();
    }
    return out;
  }
  if (!Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const item of raw) {
    const rec = asRecord(item);
    if (!rec) continue;
    const name = rec.option_display_name ?? rec.name ?? rec.option_name;
    const label = rec.label ?? rec.value ?? rec.option_value;
    if (typeof name === "string" && name.trim() && typeof label === "string" && label.trim()) {
      out[name.trim()] = label.trim();
    }
  }
  return out;
}

export function extractOptionValues(row: unknown): Record<string, string> {
  const rec = asRecord(row);
  if (!rec) return {};
  const nested = asRecord(rec.variant);
  const raw = rec.option_values ?? nested?.option_values;
  return optionMapFromUnknown(raw);
}

export function colourSizeSkuSlug(colour: string, size: string): { colour: string; size: string } {
  const colourSlug = colour.toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "");
  const sizeSlug = size.toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "");
  return { colour: colourSlug, size: sizeSlug };
}

export const DEFAULT_MATRIX_COLOURS = [
  "Pearl Teal",
  "Vibrant Lime Green",
  "Hot Pink",
  "White",
  "Black",
  "Brown",
] as const;

export const DEFAULT_MATRIX_SIZES = ["Pony", "Cob", "Full"] as const;

export function buildColourSizeFixture(opts?: {
  colours?: readonly string[];
  sizes?: readonly string[];
  quantity?: number;
  price?: number;
  currency?: string;
  skuPrefix?: string;
}): CanonicalPublicationSnapshot {
  const colours = [...(opts?.colours ?? DEFAULT_MATRIX_COLOURS)];
  const sizes = [...(opts?.sizes ?? DEFAULT_MATRIX_SIZES)];
  const quantity = opts?.quantity ?? 10;
  const price = opts?.price ?? 51.99;
  const currency = opts?.currency ?? "AUD";
  const skuPrefix = opts?.skuPrefix ?? "TRD-PVCBR";
  const variants: CanonicalVariant[] = [];
  for (const colour of colours) {
    const slug = colourSizeSkuSlug(colour, "x").colour;
    for (const size of sizes) {
      const sizeSlug = colourSizeSkuSlug("x", size).size;
      variants.push({
        sku: `${skuPrefix}-${slug}-${sizeSlug}`,
        option_values: { Colour: colour, Size: size },
        price,
        quantity,
        currency,
        image_url: `https://example.test/fixtures/${slug.toLowerCase()}.jpg`,
        gtin: null,
      });
    }
  }
  return {
    product: {
      title: "Colour × Size fixture",
      description: "Disposable publication fixture. Not a live Product Owner listing.",
      brand: "Global",
      condition: "new",
      category: "Bridle Parts & Accessories",
      weight_grams: 500,
      dimensions_cm: null,
    },
    images: variants
      .map((v) => v.image_url)
      .filter((u, i, a): u is string => !!u && a.indexOf(u) === i),
    options: [
      { name: "Colour", values: colours },
      { name: "Size", values: sizes },
    ],
    variants,
    identifier_policy: { mode: "does_not_apply", gtin: null },
  };
}

export function assertIdentifierPolicy(policy: { mode?: string; gtin?: string | null }):
  | { ok: true }
  | { ok: false; code: string; message: string } {
  const mode = policy?.mode;
  if (mode === "fabricated" || mode === "random" || mode === "generated") {
    return {
      ok: false,
      code: "GTIN_FABRICATED_FORBIDDEN",
      message: "Random or fabricated GTIN/UPC values are not supported.",
    };
  }
  if (mode !== "omitted" && mode !== "does_not_apply" && mode !== "provided") {
    return {
      ok: false,
      code: "GTIN_POLICY_INVALID",
      message: "Identifier policy must be omitted, does_not_apply, or provided.",
    };
  }
  if (mode === "provided") {
    const gtin = typeof policy.gtin === "string" ? policy.gtin.trim() : "";
    if (!gtin) {
      return {
        ok: false,
        code: "GTIN_REQUIRED_WHEN_PROVIDED",
        message: "Identifier policy provided requires a real GTIN from the Product Owner.",
      };
    }
  }
  if (policy.gtin && mode !== "provided") {
    return {
      ok: false,
      code: "GTIN_VALUE_WITHOUT_POLICY",
      message: "A GTIN value was supplied without identifier policy provided.",
    };
  }
  return { ok: true };
}

export function assertCanonicalSnapshot(snapshot: CanonicalPublicationSnapshot):
  | { ok: true; variant_count: number; total_quantity: number }
  | { ok: false; code: string; message: string; issues?: SnapshotIssue[] } {
  const id = assertIdentifierPolicy(snapshot.identifier_policy);
  if (id.ok === false) return id;

  const variants = snapshot.variants ?? [];
  if (variants.length === 0) {
    return { ok: false, code: "SNAPSHOT_EMPTY", message: "Snapshot has no explicit variants." };
  }

  const optionNames = (snapshot.options ?? []).map((o) => o.name);
  if (optionNames.length === 0) {
    return { ok: false, code: "SNAPSHOT_OPTIONS_MISSING", message: "Snapshot requires option definitions." };
  }

  const expectedCombos = snapshot.options.reduce((acc, o) => acc * Math.max(1, o.values.length), 1);
  const comboKeys = new Set<string>();
  const skus = new Set<string>();
  const issues: SnapshotIssue[] = [];
  let totalQty = 0;

  for (const v of variants) {
    if (!v.sku || typeof v.sku !== "string") {
      issues.push({ code: "SKU_MISSING", message: "Every variant must have a unique SKU." });
      continue;
    }
    if (skus.has(v.sku)) {
      issues.push({ code: "SKU_DUPLICATE", message: `Duplicate SKU ${v.sku}` });
    }
    skus.add(v.sku);

    const combo = optionNames.map((n) => `${n}=${v.option_values?.[n] ?? ""}`).join("|");
    if (comboKeys.has(combo)) {
      issues.push({ code: "COMBINATION_DUPLICATE", message: `Duplicate combination ${combo}` });
    }
    comboKeys.add(combo);

    for (const opt of snapshot.options) {
      const val = v.option_values?.[opt.name];
      if (!val || !opt.values.includes(val)) {
        issues.push({
          code: "OPTION_VALUE_MISMATCH",
          message: `SKU ${v.sku} missing authorized ${opt.name} value.`,
        });
      }
    }

    if (!Number.isInteger(v.quantity) || v.quantity < 0) {
      issues.push({ code: "QUANTITY_INVALID", message: `SKU ${v.sku} quantity must be a non-negative integer.` });
    } else {
      totalQty += v.quantity;
    }

    if (!(typeof v.price === "number") || !Number.isFinite(v.price) || v.price < 0) {
      issues.push({ code: "PRICE_INVALID", message: `SKU ${v.sku} price is invalid.` });
    }

    if (v.gtin) {
      issues.push({
        code: "GTIN_ON_VARIANT_WITHOUT_POLICY",
        message: `SKU ${v.sku} carries a GTIN; use identifier policy provided or omit it.`,
      });
    }

    if (v.image_url && !v.image_url.startsWith("https://")) {
      issues.push({ code: "IMAGE_NOT_HTTPS", message: `SKU ${v.sku} image must be HTTPS.` });
    }
  }

  if (snapshot.identifier_policy.mode === "provided") {
    const filtered = issues.filter((i) => i.code !== "GTIN_ON_VARIANT_WITHOUT_POLICY");
    issues.length = 0;
    issues.push(...filtered);
  } else {
    for (const v of variants) {
      if (v.gtin) {
        /* already recorded */
      }
    }
  }

  if (comboKeys.size !== expectedCombos && issues.every((i) => i.code !== "COMBINATION_DUPLICATE")) {
    issues.push({
      code: "MATRIX_INCOMPLETE",
      message: `Expected ${expectedCombos} explicit combinations, found ${comboKeys.size}.`,
    });
  }

  if (issues.length > 0) {
    return { ok: false, code: issues[0].code, message: issues[0].message, issues };
  }

  return { ok: true, variant_count: variants.length, total_quantity: totalQty };
}

export function canonicalFromListingPayload(payload: Record<string, unknown>): CanonicalPublicationSnapshot {
  const listing = asRecord(payload.listing) ?? {};
  const product = pickProductForProvider("ebay", asRecord(payload.product) ?? {});
  const rawVariants = Array.isArray(payload.variants) ? payload.variants : [];
  const rawOptions = Array.isArray(payload.options) ? payload.options : [];
  const media = Array.isArray(payload.media) ? payload.media : [];

  const options: CanonicalOptionDefinition[] = rawOptions.map((row) => {
    const rec = asRecord(row) ?? {};
    const values = Array.isArray(rec.values)
      ? rec.values.map((v) => {
        if (typeof v === "string") return v;
        const vr = asRecord(v);
        return typeof vr?.value === "string" ? vr.value : "";
      }).filter(Boolean)
      : [];
    return { name: String(rec.name ?? ""), values };
  }).filter((o) => o.name);

  const variants: CanonicalVariant[] = rawVariants.map((row) => {
    const rec = asRecord(row) ?? {};
    const nested = asRecord(rec.variant) ?? {};
    const ov = extractOptionValues(rec);
    const sku = String(rec.channel_sku || rec.sku || nested.sku || "");
    const price = Number(
      rec.effective_channel_price
        ?? rec.channel_price
        ?? effectiveSellingPrice(
          typeof nested.selling_price === "number" ? nested.selling_price : (typeof rec.selling_price === "number" ? rec.selling_price : null),
          typeof product.selling_price === "number" ? product.selling_price : null,
        )
        ?? 0,
    );
    const qtyRaw = rec.available_quantity ?? rec.quantity ?? rec.on_hand;
    const quantity = Number.isInteger(Number(qtyRaw)) ? Number(qtyRaw) : 0;
    const image = typeof rec.image_url === "string" ? rec.image_url
      : typeof nested.image_url === "string" ? nested.image_url
      : null;
    return {
      id: typeof rec.variant_id === "string" ? rec.variant_id : (typeof rec.id === "string" ? rec.id : null),
      sku,
      option_values: ov,
      price,
      quantity,
      currency: String(listing.currency || payload.currency || "AUD"),
      image_url: image,
      gtin: null,
    };
  });

  if (options.length === 0 && variants.length > 0) {
    const names = [...new Set(variants.flatMap((v) => Object.keys(v.option_values)))];
    for (const name of names) {
      options.push({
        name,
        values: [...new Set(variants.map((v) => v.option_values[name]).filter(Boolean))],
      });
    }
  }

  const images = media.map((m) => {
    const rec = asRecord(m);
    const url = rec?.url ?? rec?.public_url ?? rec?.src ?? rec?.image_url;
    return typeof url === "string" ? url : "";
  }).filter((u) => u.startsWith("https://"));

  const identifierModeRaw = asRecord(payload.identifier_policy)?.mode;
  const mode: IdentifierPolicyMode =
    identifierModeRaw === "provided" || identifierModeRaw === "omitted" || identifierModeRaw === "does_not_apply"
      ? identifierModeRaw
      : "does_not_apply";

  const productDims = {
    weightKg: typeof product.weight_kg === "number" ? product.weight_kg : null,
    lengthCm: typeof product.length_cm === "number" ? product.length_cm : null,
    widthCm: typeof product.width_cm === "number" ? product.width_cm : null,
    heightCm: typeof product.height_cm === "number" ? product.height_cm : null,
  };
  const firstNested = asRecord(asRecord(rawVariants[0])?.variant) ?? asRecord(rawVariants[0]) ?? {};
  const firstVariantDims = {
    weightKg: typeof firstNested.weight_kg === "number" ? firstNested.weight_kg : null,
    lengthCm: typeof firstNested.length_cm === "number" ? firstNested.length_cm : null,
    widthCm: typeof firstNested.width_cm === "number" ? firstNested.width_cm : null,
    heightCm: typeof firstNested.height_cm === "number" ? firstNested.height_cm : null,
  };
  const shipping = resolveShippingFacts(firstVariantDims, productDims);

  return {
    product: {
      title: String(listing.channel_title || product.name || ""),
      description: String(listing.channel_description || product.description || ""),
      brand: typeof product.brand === "string" ? product.brand : null,
      mpn: typeof product.mpn === "string" && product.mpn.trim() ? product.mpn.trim() : null,
      condition: typeof product.condition === "string" && /^new$/i.test(product.condition.trim())
        ? "new"
        : (typeof product.condition === "string" && product.condition.trim() ? product.condition.trim() : "new"),
      category: typeof listing.channel_category_id === "string" ? listing.channel_category_id : null,
      weight_grams: shipping.weightKg != null ? Math.round(shipping.weightKg * 1000) : null,
      dimensions_cm: shipping.lengthCm != null && shipping.widthCm != null && shipping.heightCm != null
        ? { length: shipping.lengthCm, width: shipping.widthCm, height: shipping.heightCm }
        : null,
    },
    images,
    options,
    variants,
    identifier_policy: { mode, gtin: null },
    storefront: {
      seo_title: approvedOverrideThenCanonical(
        typeof listing.channel_seo_title === "string" ? listing.channel_seo_title : null,
        typeof product.seo_title === "string" ? product.seo_title : null,
      ),
      meta_description: approvedOverrideThenCanonical(
        typeof listing.channel_meta_description === "string" ? listing.channel_meta_description : null,
        typeof product.meta_description === "string" ? product.meta_description : null,
      ),
      seo_keywords: approvedOverrideThenCanonical(
        typeof listing.channel_seo_keywords === "string" ? listing.channel_seo_keywords : null,
        typeof product.seo_keywords === "string" ? product.seo_keywords : null,
      ),
      url_slug: approvedOverrideThenCanonical(
        typeof listing.channel_url_slug === "string" ? listing.channel_url_slug : null,
        typeof product.url_slug === "string" ? product.url_slug : null,
      ),
      og_title: approvedOverrideThenCanonical(
        typeof listing.channel_og_title === "string" ? listing.channel_og_title : null,
        typeof product.og_title === "string" ? product.og_title : null,
      ),
      og_description: approvedOverrideThenCanonical(
        typeof listing.channel_og_description === "string" ? listing.channel_og_description : null,
        typeof product.og_description === "string" ? product.og_description : null,
      ),
      og_image_url: typeof product.og_image_url === "string" && product.og_image_url.trim() ? product.og_image_url.trim() : null,
      storefront_visible: approvedOverrideThenCanonicalBool(
        typeof listing.channel_storefront_visible === "boolean" ? listing.channel_storefront_visible : null,
        typeof product.storefront_visible === "boolean" ? product.storefront_visible : null,
        true,
      ),
      is_featured: approvedOverrideThenCanonicalBool(
        typeof listing.channel_is_featured === "boolean" ? listing.channel_is_featured : null,
        typeof product.is_featured === "boolean" ? product.is_featured : null,
        false,
      ),
      min_purchase_qty: approvedOverrideThenCanonicalInt(null, product.min_purchase_qty != null ? Number(product.min_purchase_qty) : null),
      max_purchase_qty: approvedOverrideThenCanonicalInt(null, product.max_purchase_qty != null ? Number(product.max_purchase_qty) : null),
    },
  };
}
